import React, { PropsWithChildren, useMemo } from 'react';
import classNames from 'classnames';
import { CSeverity, NativeProps } from '@/src/components/interfaces';
import { AlignItems, CFlex, JustifyContent } from '@/src/components/c-flex/CFlex';
import { CBox } from '@/src/components/c-box/CBox';
import { CIcon, CIconColor, CIcons, CIconSize } from '@/src/components/c-icon/CIcon';

export interface CAlertProps extends NativeProps {
  severity?: CSeverity;
  onClose?: () => (Promise<void> | void);
  showIcon?: boolean;
}

export function CAlert(props: PropsWithChildren<CAlertProps>): React.JSX.Element {
  const severity = props.severity ?? CSeverity.Info;
  const showIcon = props.showIcon ?? false;

  const icon = useMemo((): CIcons => {
    const map = {
      [CSeverity.Success]: CIcons.CheckCircle,
      [CSeverity.Warning]: CIcons.AlertCircle,
      [CSeverity.Error]: CIcons.XCircle,
      [CSeverity.Info]: CIcons.Info,
    };

    return map[severity] ?? map[CSeverity.Info];
  }, [severity]);

  const iconColor = useMemo((): CIconColor => {
    const map = {
      [CSeverity.Success]: CIconColor.Success,
      [CSeverity.Warning]: CIconColor.Warning,
      [CSeverity.Error]: CIconColor.Error,
      [CSeverity.Info]: CIconColor.Info,
    };

    return map[severity] ?? map[CSeverity.Info];
  }, [severity]);

  return (
    <CBox
      class={classNames('c-alert', `c-alert--severity-${severity}`, props.class)}
      style={props.style}
      id={props.id}
      data-test-selector={props.testSelector}
    >
      <CFlex
        class="c-gap--md"
        justifyContent={JustifyContent.SpaceBetween}
        alignItems={AlignItems.FlexStart}
      >
        <CBox>
          <CFlex
            class="c-gap--md"
            alignItems={AlignItems.FlexStart}
          >
            {showIcon && (
              <CIcon
                icon={icon}
                size={CIconSize.H2}
                color={iconColor}
              />
            )}
            <div>
              {props.children}
            </div>
          </CFlex>
        </CBox>
        {props.onClose && (
          <CIcon
            class="cursor--pointer tap--disabled"
            icon={CIcons.X}
            size={CIconSize.H2}
            color={CIconColor.Gray1}
            onClick={props.onClose}
          />
        )}
      </CFlex>
    </CBox>
  );
}